import { useState, useMemo } from "react";
import { IC, Avatar } from "../shared";

// ── iOS Detection ─────────────────────────────────────────────────────────────
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

// ── Nav Badge ─────────────────────────────────────────────────────────────────
export const NavBadge = ({ count }) => count > 0 ? (
  <span style={{
    position: "absolute", top: -3, right: -3,
    background: "#e63946", color: "#fff",
    borderRadius: 10, fontSize: 9, fontWeight: 700, fontFamily: "Syne",
    padding: "1px 5px", border: "2px solid var(--paper)",
    minWidth: 16, textAlign: "center", lineHeight: "14px",
  }}>{count > 99 ? "99+" : count}</span>
) : null;

// ── Bottom Nav ────────────────────────────────────────────────────────────────
export const BottomNav = ({ tab, setTab, unreadCount = 0, chatCount = 0 }) => {
  const [tapped, setTapped] = useState(null);
  const ios = useMemo(() => isIOS(), []);
  const standalone = useMemo(() => typeof window !== "undefined" && (window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches), []);
  const tabs = [
    { id: "find",    icon: IC.search, label: "Find" },
    { id: "post",    icon: IC.car,    label: "Post" },
    { id: "trips",   icon: IC.seat,   label: "Trips" },
    { id: "chat",    icon: IC.chat,   label: "Chat",   badge: chatCount },
    { id: "notifs",  icon: IC.bell,   label: "Alerts", badge: unreadCount },
    { id: "profile", icon: IC.user,   label: "Me" },
  ];
  const handleTab = (id) => {
    setTapped(id);
    setTab(id);
    setTimeout(() => setTapped(null), 400);
  };

  // Default style (Android / Desktop)
  const defaultNavStyle = {
    position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
    width: "100%", maxWidth: 430,
    background: "var(--nav-blur)",
    backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
    borderTop: "1px solid var(--border)",
    display: "flex", padding: "8px 4px 14px", zIndex: 100,
  };

  // Apple Liquid Glass style (iOS only)
  // In PWA standalone: pin to bottom edge, full width — safe area handled by CSS class
  // In Safari: floating pill with rounded corners
  // Face ID iPhones (X and later) have 34px home indicator area
  const hasHomeIndicator = ios && window.screen.height >= 812;
  const safeBottom = (standalone && hasHomeIndicator) ? 34 : 0;

  const liquidGlassStyle = standalone ? {
    position: "fixed", bottom: 0, left: 0, right: 0,
    width: "100%",
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    borderTop: "1px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 -4px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
    display: "flex", padding: `8px 4px ${10 + safeBottom}px`, zIndex: 100,
  } : {
    position: "fixed", bottom: 8, left: "50%", transform: "translateX(-50%)",
    width: "calc(100% - 16px)", maxWidth: 414,
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: 30,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
    display: "flex", padding: "8px 4px 10px", zIndex: 100,
  };

  const navClass = `bottom-nav${ios ? " liquid-glass" : ""}${standalone ? " pwa-standalone" : ""}`;

  return (
    <nav className={navClass} style={ios ? liquidGlassStyle : defaultNavStyle}>
      {tabs.map(t => {
        const active = tab === t.id;
        return (
          <button key={t.id} onClick={() => handleTab(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, position: "relative", color: active ? "var(--ink)" : "var(--muted)", transition: "color .15s", padding: "4px 0" }}>
            {active && <div style={{ position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)", width: 28, height: 3, borderRadius: 3, background: "linear-gradient(90deg,#2b5be0,#5a80f0)" }}/>}
            <div className={tapped === t.id ? "abounce" : ""} style={{ width: 36, height: 36, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: active ? "rgba(43,91,224,.10)" : "transparent", transition: "background .15s", position: "relative" }}>
              <div style={{ width: 20, height: 20 }}>{t.icon}</div>
              <NavBadge count={t.badge}/>
            </div>
            <span style={{ fontSize: 10, fontFamily: "Syne", fontWeight: active ? 700 : 500 }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

// ── Desktop Top Nav ───────────────────────────────────────────────────────────
export const DesktopTopNav = ({ tab, setTab, unreadCount, chatCount, user }) => (
  <header className="desktop-topnav">
    <div className="desk-nav-inner">
      <div className="desk-nav-logo">iGo</div>
      <nav className="desk-nav-links">
        {[["find","Find a Ride"],["post","Post a Trip"],["trips","My Trips"],["chat","Chat"],["notifs","Alerts"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={`desk-nav-link${tab === id ? " active" : ""}`}>
            {label}
            {id === "notifs" && unreadCount > 0 && <span className="desk-nav-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
            {id === "chat"   && chatCount   > 0 && <span className="desk-nav-badge">{chatCount   > 99 ? "99+" : chatCount}</span>}
          </button>
        ))}
      </nav>
      {user && (
        <button className="desk-nav-user" onClick={() => setTab("profile")}>
          <Avatar user={user} size={28}/>
          <span>{user.name}</span>
        </button>
      )}
    </div>
  </header>
);

export default BottomNav;
